(function () {
  'use strict';

  function readData(element, property, attribute) {
    const value = element?.dataset?.[property] ?? element?.getAttribute?.(attribute);
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  function getGalleryKey(element) {
    return readData(element, 'galleryKey', 'data-gallery-key');
  }

  function getManifestOrder(gallery) {
    const value = readData(gallery, 'manifestOrder', 'data-manifest-order');
    if (!value) return [];
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function getManifestMediaId(element) {
    const explicit = readData(element, 'manifestMediaId', 'data-manifest-media-id');
    if (explicit) return explicit;

    const fallback = readData(element, 'mediaId', 'data-media-id') || readData(element, 'target', 'data-target');
    const match = String(fallback || '').match(/(\d+)$/);
    return match?.[1] || null;
  }

  function orderedNodes(list, order, featuredMediaId) {
    if (!list?.children || !order.length) return null;

    const nodes = Array.from(list.children);
    const byMediaId = new Map(
      nodes
        .map((node) => [getManifestMediaId(node), node])
        .filter(([mediaId]) => mediaId),
    );
    const canonical = order.map((mediaId) => byMediaId.get(mediaId)).filter(Boolean);
    const remaining = nodes.filter((node) => !canonical.includes(node));
    const featured = byMediaId.get(String(featuredMediaId || ''));
    const withoutFeatured = canonical.filter((node) => node !== featured);
    const arranged = featured ? [featured, ...withoutFeatured, ...remaining] : [...canonical, ...remaining];

    arranged.forEach((node) => list.appendChild(node));
    return { featured, nodes: arranged };
  }

  function promoteManifestFeaturedMedia(gallery, featuredMediaId) {
    if (!gallery || !featuredMediaId) return { status: 'unsupported', gallery };

    const order = getManifestOrder(gallery);
    const viewerList = gallery.querySelector?.('.product__media-list');
    const thumbnailList = gallery.querySelector?.('.thumbnail-list');
    if (!order.length || !viewerList || !thumbnailList) {
      return { status: 'unsupported', gallery };
    }

    const viewer = orderedNodes(viewerList, order, featuredMediaId);
    const thumbnails = orderedNodes(thumbnailList, order, featuredMediaId);
    if (!viewer?.featured || !thumbnails?.featured) {
      return { status: 'missing-featured', gallery };
    }

    viewer.nodes.forEach((node) => node.classList?.remove('is-active'));
    viewer.featured.classList?.add('is-active');
    thumbnails.nodes.forEach((node) => {
      const button = node.querySelector?.('button');
      button?.removeAttribute('aria-current');
    });
    thumbnails.featured.querySelector?.('button')?.setAttribute('aria-current', 'true');

    const sectionId = readData(gallery, 'sectionId', 'data-section-id');
    if (sectionId) gallery.setActiveMedia?.(`${sectionId}-${featuredMediaId}`, true);
    if ('scrollLeft' in viewerList) viewerList.scrollLeft = 0;
    if ('scrollLeft' in thumbnailList) thumbnailList.scrollLeft = 0;
    initThumbnailSliderFallback(gallery);

    return { status: 'promoted', gallery, featuredMediaId: String(featuredMediaId) };
  }

  function isUsableGallery(element) {
    return Boolean(element && typeof element.replaceWith === 'function');
  }

  function shouldReplaceGallery(current, incoming) {
    if (!isUsableGallery(current) || !isUsableGallery(incoming)) return false;

    const currentKey = getGalleryKey(current);
    const incomingKey = getGalleryKey(incoming);
    return currentKey !== incomingKey && Boolean(currentKey || incomingKey);
  }

  function replaceManifestGallery(current, incoming) {
    const currentKey = getGalleryKey(current);
    const incomingKey = getGalleryKey(incoming);
    const featuredMediaId = readData(incoming, 'featuredMediaId', 'data-featured-media-id');

    if (!isUsableGallery(current) || !isUsableGallery(incoming) || (!currentKey && !incomingKey)) {
      return { status: 'unsupported', replaced: false, gallery: current, featuredMediaId };
    }

    if (currentKey === incomingKey) {
      return { status: 'same-group', replaced: false, gallery: current, featuredMediaId };
    }

    current.replaceWith(incoming);
    return { status: 'replaced', replaced: true, gallery: incoming, featuredMediaId };
  }

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getFilenameFromPath(path) {
    const normalizedPath = String(path || '').split('?')[0].split('#')[0];
    return normalizedPath.substring(normalizedPath.lastIndexOf('/') + 1);
  }

  function parseMediaFilename(path) {
    const match = getFilenameFromPath(path).match(/^([a-z0-9-]+)_(small|large|all)_/i);
    if (!match) return null;

    return {
      colorKey: normalizeText(match[1]),
      sizeGroup: normalizeText(match[2]),
    };
  }

  function readSelectedVariant(gallery) {
    const productInfo = gallery?.closest?.('product-info') || document;
    const selected = productInfo.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
    if (!selected) return null;

    try {
      return JSON.parse(selected);
    } catch (error) {
      return null;
    }
  }

  function findSelectedValueByOptionName(gallery, optionKeywords) {
    const productInfo = gallery?.closest?.('product-info') || document;
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return '';

    for (const field of variantSelects.querySelectorAll('.product-form__input')) {
      const label = normalizeText(field.querySelector('legend, label.form__label')?.textContent);
      if (!optionKeywords.some((keyword) => label.includes(keyword))) continue;

      const checked = field.querySelector('input:checked');
      const selected = field.querySelector('select option:checked');
      return normalizeText(checked?.value || selected?.value);
    }

    return '';
  }

  function getSelectedColor(gallery, variant) {
    return (
      findSelectedValueByOptionName(gallery, ['color', 'colour', 'pattern', '颜色', '款式']) ||
      normalizeText(variant?.option1)
    );
  }

  function getSelectedSize(gallery, variant) {
    return (
      findSelectedValueByOptionName(gallery, ['size', '尺寸', '尺码']) ||
      normalizeText(variant?.option2 || variant?.option3 || variant?.option1)
    );
  }

  function updateGalleryByFilename(gallery) {
    if (!gallery || getGalleryKey(gallery)) return;

    const variant = readSelectedVariant(gallery);
    const selectedColor = getSelectedColor(gallery, variant);
    const selectedSize = getSelectedSize(gallery, variant);
    if (!selectedColor || !selectedSize) return;

    const currentSizeGroup = ['2xl', '3xl', '4xl'].includes(selectedSize) ? 'large' : 'small';
    const filteredMediaIds = new Set();

    gallery.querySelectorAll('[data-image-src]').forEach((item) => {
      const parsed = parseMediaFilename(item.getAttribute('data-image-src'));
      if (!parsed) return;

      const shouldShow =
        parsed.sizeGroup === 'all' ||
        (parsed.colorKey === selectedColor && parsed.sizeGroup === currentSizeGroup);

      item.hidden = !shouldShow;
      item.classList.toggle('filename-gallery-hidden', !shouldShow);

      const mediaId = item.dataset.mediaId || item.dataset.target;
      if (shouldShow && mediaId) filteredMediaIds.add(mediaId);
    });

    const firstVisibleMediaId = Array.from(filteredMediaIds)[0];
    const activeMedia = gallery.querySelector('[data-media-id].is-active');
    if (firstVisibleMediaId && (!activeMedia || activeMedia.hidden)) {
      gallery.setActiveMedia?.(firstVisibleMediaId, true);
    }
  }

  // Manifest sorting can replace or hide thumbnail nodes after Dawn's slider
  // has initialized. Keep the gallery arrows usable from the current DOM.
  function initThumbnailSliderFallback(gallery) {
    const slider = gallery?.querySelector?.('.thumbnail-slider');
    const list = slider?.querySelector?.('.thumbnail-list');
    const previous = slider?.querySelector?.('button[name="previous"]');
    const next = slider?.querySelector?.('button[name="next"]');
    if (!slider || !list || !previous || !next || slider._manifestSliderFallback) return;

    slider._manifestSliderFallback = true;

    const getVisibleItems = () => Array.from(list.children).filter((item) => {
      const style = window.getComputedStyle(item);
      return !item.hidden && style.display !== 'none' && item.offsetWidth > 0;
    });

    const getStep = () => {
      const items = getVisibleItems();
      if (items.length < 2) return items[0]?.offsetWidth || 0;
      return items[1].offsetLeft - items[0].offsetLeft || items[0].offsetWidth;
    };

    const updateButtons = () => {
      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      previous.disabled = list.scrollLeft <= 1;
      next.disabled = list.scrollLeft >= maxScroll - 1;
    };

    const move = (event) => {
      event.preventDefault();
      const step = getStep();
      if (!step) return;
      const count = Number(event.currentTarget.dataset.step) || 1;
      const direction = event.currentTarget.name === 'next' ? 1 : -1;
      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      const position = Math.max(0, Math.min(maxScroll, list.scrollLeft + direction * count * step));
      list.scrollTo({ left: position, behavior: 'smooth' });
    };

    // Capture the click before Dawn's handler so a stale Manifest slider state
    // cannot cancel the reverse movement.
    slider.addEventListener('click', (event) => {
      const button = event.target.closest?.('button[name="previous"], button[name="next"]');
      if (button !== previous && button !== next) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      move({ currentTarget: button, preventDefault() {} });
    }, true);
    list.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
  }

  const api = { getGalleryKey, shouldReplaceGallery, replaceManifestGallery, updateGalleryByFilename };
  api.promoteManifestFeaturedMedia = promoteManifestFeaturedMedia;

  if (typeof window !== 'undefined') {
    window.ManifestGallery = api;
    if (typeof document !== 'undefined') {
      document.addEventListener('DOMContentLoaded', () => {
      document.querySelectorAll('media-gallery').forEach((gallery) => {
        promoteManifestFeaturedMedia(gallery, readData(gallery, 'featuredMediaId', 'data-featured-media-id'));
        updateGalleryByFilename(gallery);
        initThumbnailSliderFallback(gallery);
      });
      });
    }
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports.getGalleryKey = getGalleryKey;
    module.exports.shouldReplaceGallery = shouldReplaceGallery;
    module.exports.replaceManifestGallery = replaceManifestGallery;
    module.exports.updateGalleryByFilename = updateGalleryByFilename;
    module.exports.promoteManifestFeaturedMedia = promoteManifestFeaturedMedia;
  }
})();
